import React from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle2,
  Circle,
  Building2,
  Telescope,
  Workflow,
  GitBranch,
  ArrowRight,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import '../styles/SetupPanel.css';

// SetupPanel — the dashboard's "Set up this engagement" panel (028-engagement-setup
// E1). A THIN, PROP-DRIVEN view (FR-009): it derives NOTHING about completeness
// itself. The dashboard already runs `deriveProgramme(...)` and reads the
// engagement snapshot, and passes the result down as props. The panel reports
// the three EXISTING `programmeModel` setup items (`setup-org`/`setup-vision`/
// `setup-areas`) and links to the three existing surfaces — it is an
// orchestrator/reporter, not a wizard and not a second derivation path.
//
// Honesty (FR-003/SC-003): because the state arrives as PROPS and the panel
// renders purely from them, there is no place for a cached/sticky completion
// flag. The count + collapse recompute on every render from the props alone.
//
// Reserved-colour discipline (FR-009/SC-007): the setup steps are everyday-blue
// warm-ups. Coral (`--ap-accent`) is RESERVED for 027's first-run "Create
// engagement" CTA and is NEVER spent here. lucide icons + index.css tokens only.

export type SetupRowState = 'done' | 'todo';

export type SetupItems = {
  // 032: the REQUIRED knowledge-repository step is FIRST (before the org row).
  'setup-repo': SetupRowState;
  'setup-org': SetupRowState;
  'setup-vision': SetupRowState;
  'setup-areas': SetupRowState;
};

export type SetupFacts = {
  /** organisation name, null/'' when unset */
  orgName: string | null;
  /** vision goals captured */
  goalCount: number;
  /** vision cover story confirmed */
  coverStoryConfirmed: boolean;
  /** candidate value streams (with an SME) */
  streamCount: number;
  /** linked knowledge-repo owner/org, null when unlinked (032) */
  repoOwner: string | null;
  /** linked knowledge-repo name, null when unlinked (032) */
  repoName: string | null;
};

export type SetupPanelProps = {
  items: SetupItems;
  facts: SetupFacts;
};

type SetupRowId = keyof SetupItems;

type RowDef = {
  id: SetupRowId;
  /** The visible row label + the accessible name of its surface link. */
  label: string;
  /**
   * The existing surface this step links to. Optional since the 054 T052
   * cutover: the vision step's surface is session-keyed
   * (/facilitation/:sessionId) and not bare-linkable, so its row carries no
   * link — honest absence beats a dead link (cutover-ruling.md, Ruling 3
   * detector 5; the replacement entry-point shape is human-acked at T054).
   */
  route?: string;
  /** The lucide glyph for this row. */
  Glyph: LucideIcon;
  /** The one-line "why" helper beneath the title. */
  why: string;
  /** The advancing-primary verb for this row ("Set up" / "Connect"). */
  cta: string;
  /** The live one-line summary, derived from the snapshot facts (done rows only). */
  summary: (facts: SetupFacts) => string;
};

function plural(n: number, word: string): string {
  if (word === 'background confirmed') return word;
  return `${n} ${word}${n === 1 ? '' : 's'}`;
}

// The rows in FIXED order. The REQUIRED knowledge-repository row is FIRST (032,
// FR-005) — it links to /knowledge-repo and has NO skip/defer affordance. The
// streams row's LABEL is the panel's own copy "Candidate value streams" (NOT the
// ScopeWidget rename) — it links to /streams.
const ROWS: RowDef[] = [
  {
    id: 'setup-repo',
    label: 'Knowledge repository',
    route: '/knowledge-repo',
    Glyph: GitBranch,
    why: 'The git-backed home the generated knowledge corpus lands in — required.',
    cta: 'Connect',
    summary: (f) =>
      f.repoOwner && f.repoName ? `${f.repoOwner}/${f.repoName}` : 'Repository linked',
  },
  {
    id: 'setup-org',
    label: 'Organisation profile',
    route: '/organisation',
    Glyph: Building2,
    why: 'Who the client is and the context that frames the engagement.',
    cta: 'Set up',
    summary: (f) =>
      `${f.orgName && f.orgName.trim() ? f.orgName.trim() : 'Organisation'} · ${plural(
        1,
        'background confirmed',
      )}`,
  },
  {
    id: 'setup-vision',
    label: 'Vision',
    // No route since T052: the facilitated vision session is session-keyed
    // and not bare-linkable (see the RowDef.route note above).
    Glyph: Telescope,
    why: 'What success looks like, in the sponsor’s words, and the failure cases to guard.',
    cta: 'Set up',
    summary: (f) => `${plural(f.goalCount, 'goal')} · cover story confirmed`,
  },
  {
    id: 'setup-areas',
    label: 'Candidate value streams',
    route: '/streams',
    Glyph: Workflow,
    why: 'The streams a later loop attaches to — start with one small one.',
    cta: 'Set up',
    summary: (f) => `${plural(f.streamCount, 'candidate stream')}`,
  },
];

const PANEL_TITLE_ID = 'setup-panel-title';

// A single setup row. `primary` marks the first incomplete row, which carries the
// SINGLE advancing "Set up →" CTA; done rows carry a quieter "Review" link and a
// live summary. State is conveyed by glyph + an sr-only word, never colour alone.
const SetupRow: React.FC<{ row: RowDef; state: SetupRowState; facts: SetupFacts; primary: boolean }> = ({
  row,
  state,
  facts,
  primary,
}) => {
  const done = state === 'done';
  const { Glyph } = row;
  return (
    <li className={`ap-setup-row ap-setup-row--${state}`}>
      <span className="ap-setup-row-state" aria-hidden="true">
        {done ? (
          <CheckCircle2 size={20} className="ap-setup-row-tick" />
        ) : (
          <Circle size={20} className="ap-setup-row-circle" />
        )}
      </span>
      <span className="ap-setup-row-body">
        <span className="ap-setup-row-titleline">
          <Glyph size={15} aria-hidden="true" />
          {row.route ? (
            <Link to={row.route} className="ap-setup-row-title">
              {row.label}
            </Link>
          ) : (
            <span className="ap-setup-row-title">{row.label}</span>
          )}
          {/* Done-ness as a real, screen-reader word (never colour alone). */}
          <span className="ap-setup-sr-only">{done ? 'Done' : 'To do'}</span>
        </span>
        <span className="ap-setup-row-why">{row.why}</span>
        {done ? <span className="ap-setup-row-summary">{row.summary(facts)}</span> : null}
      </span>
      <span className="ap-setup-row-action">
        {done && row.route ? (
          <Link to={row.route} className="ap-setup-review">
            Review
          </Link>
        ) : !done && primary && row.route ? (
          <Link to={row.route} className="ap-setup-primary">
            {row.cta}
            <ArrowRight size={15} aria-hidden="true" />
          </Link>
        ) : null}
      </span>
    </li>
  );
};

const SetupPanel: React.FC<SetupPanelProps> = ({ items, facts }) => {
  const states = ROWS.map((row) => ({ row, state: items[row.id] }));
  const doneCount = states.filter((s) => s.state === 'done').length;
  const allDone = doneCount === ROWS.length;
  // The single advancing primary lands on the FIRST incomplete row (FR-002).
  const firstTodoIndex = states.findIndex((s) => s.state !== 'done');

  return (
    <section className="ap-setup" aria-labelledby={PANEL_TITLE_ID}>
      <div className="ap-setup-head">
        <h2 id={PANEL_TITLE_ID} className="ap-setup-title">
          Set up this engagement
        </h2>
        <span className="ap-setup-count">
          {doneCount} of {ROWS.length} set up
        </span>
      </div>

      <div className="ap-setup-rail" aria-hidden="true">
        {states.map((s) => (
          <span
            key={s.row.id}
            className={`ap-setup-rail-dot${s.state === 'done' ? ' ap-setup-rail-dot--on' : ''}`}
          />
        ))}
      </div>

      {allDone ? (
        <div className="ap-setup-revisit">
          <CheckCircle2 size={18} className="ap-setup-revisit-tick" aria-hidden="true" />
          <span className="ap-setup-revisit-label">Engagement set up · revisit</span>
          <Link to="/streams" className="ap-setup-revisit-link">
            Review streams
          </Link>
        </div>
      ) : (
        <ul className="ap-setup-rows">
          {states.map((s, i) => (
            <SetupRow
              key={s.row.id}
              row={s.row}
              state={s.state}
              facts={facts}
              primary={i === firstTodoIndex}
            />
          ))}
        </ul>
      )}
    </section>
  );
};

export default SetupPanel;
